/* Una forma sola per le operazioni, da qualunque parte arrivino.
 *
 * Un backtest e un tradebot raccontano la stessa cosa — è entrato qui, è
 * uscito là, è andata così — ma con due forme diverse: il backtest ha
 * `TradeBacktest` con campi sempre valorizzati, il bot ha `Trade`, dove quasi
 * tutto può essere `null` perché un'operazione può essere ancora aperta, o
 * fallita a metà.
 *
 * La normalizzazione avviene QUI, all'ingresso, e non dentro i grafici: un
 * grafico che sappia distinguere un backtest da un bot è un grafico che prima o
 * poi li disegnerà in due modi diversi, ed è esattamente il confronto che si
 * vuole poter fare — i segnali veri di un bot contro quelli della prova da cui
 * è nato.
 *
 * Regola che attraversa tutto il file: `null` significa «non c'è», mai zero.
 * Un'operazione aperta non ha un esito dello 0%: non ha un esito.
 */

import type { Backtest, Trade, TradeBacktest, Tradebot } from '../api/tipi';
import { periodo as etichettaPeriodo } from './formato';

export interface PuntoPrezzo {
  timestamp: number;
  close: number;
}

export interface PuntoEquity {
  timestamp: number;
  value: number;
}

export interface OperazioneNormalizzata {
  /** ISO. Un'operazione senza data di entrata non è disegnabile e viene scartata. */
  entrataIl: string;
  prezzoEntrata: number | null;
  /** `null` quando l'operazione è ancora aperta: è lo stato normale di un bot. */
  uscitaIl: string | null;
  prezzoUscita: number | null;
  /** Risultato in percentuale. `null` finché l'operazione non è chiusa. */
  esito: number | null;
  capitaleEntrata: number | null;
  capitaleUscita: number | null;
  nota: string | null;
}

export interface SerieRisultato {
  /** Da dove vengono i dati: cambia solo ciò che si può DIRE, non come si disegna. */
  origine: 'backtest' | 'tradebot';
  operazioni: OperazioneNormalizzata[];
  /** Serie dei prezzi. `null` quando non esiste: il bot non ne registra una. */
  prezzi: PuntoPrezzo[] | null;
  /** Curva del capitale. `null` quando non esiste. */
  equity: PuntoEquity[] | null;
  drawdownMax: number | null;
  coppia: string;
  periodo: string;
  /** Quante operazioni il produttore dichiara di aver fatto, anche se non le
   *  ha salvate: serve a distinguere «non ha operato» da «non le ha scritte». */
  operazioniDichiarate: number | null;
}

function numero(v: unknown): number | null {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function daTradeBacktest(t: TradeBacktest): OperazioneNormalizzata | null {
  if (!t.buyDate) return null;
  return {
    entrataIl: t.buyDate,
    prezzoEntrata: numero(t.buyPrice),
    uscitaIl: t.sellDate ?? null,
    prezzoUscita: numero(t.sellPrice),
    esito: numero(t.change),
    capitaleEntrata: numero(t.entryBalance),
    capitaleUscita: numero(t.exitBalance),
    nota: t.comment?.trim() ? t.comment : null,
  };
}

function daTradeBot(t: Trade): OperazioneNormalizzata | null {
  if (!t.buyDate) return null;
  return {
    entrataIl: t.buyDate,
    prezzoEntrata: numero(t.buyPrice),
    uscitaIl: t.sellDate,
    prezzoUscita: numero(t.sellPrice),
    /* Il bot scrive `change` solo alla chiusura: su un'operazione aperta resta
       null, e null deve restare. */
    esito: t.sellDate ? numero(t.change) : null,
    capitaleEntrata: numero(t.entryBalance),
    capitaleUscita: numero(t.exitBalance),
    nota: t.comment?.trim() ? t.comment : null,
  };
}

/** Le operazioni e le serie di un backtest. */
export function serieDaBacktest(b: Backtest): SerieRisultato {
  const operazioni = (b.trades ?? [])
    .map(daTradeBacktest)
    .filter((o): o is OperazioneNormalizzata => o != null);

  return {
    origine: 'backtest',
    operazioni,
    prezzi: b.candlesClosePrices?.length ? b.candlesClosePrices : null,
    equity: b.equityLinePoints?.length ? b.equityLinePoints : null,
    drawdownMax: b.drawdownMaxValue,
    coppia: `${b.asset}/${b.currency}`,
    periodo: etichettaPeriodo(b.startDate, b.endDate),
    operazioniDichiarate: b.tradesNumber ?? null,
  };
}

/**
 * Le operazioni di un tradebot.
 *
 * Il bot non registra né la serie dei prezzi né la curva del capitale, quindi
 * `prezzi` va passato da fuori se lo si ha (per esempio quello del backtest da
 * cui il bot è nato: è quel confronto — segnali veri contro segnali di prova —
 * la ragione per cui questo componente serve anche lì).
 *
 * Le operazioni di un bot sono POCHISSIME: quello in produzione ne ha una,
 * aperta e non ancora chiusa. Una sola riga e un solo marcatore sono lo stato
 * normale, non un guasto.
 */
export function serieDaTradebot(
  bot: Tradebot,
  extra: { prezzi?: PuntoPrezzo[] | null } = {},
): SerieRisultato {
  const operazioni = (bot.trades ?? [])
    .map(daTradeBot)
    .filter((o): o is OperazioneNormalizzata => o != null)
    .sort((a, b) => new Date(a.entrataIl).getTime() - new Date(b.entrataIl).getTime());

  return {
    origine: 'tradebot',
    operazioni,
    prezzi: extra.prezzi?.length ? extra.prezzi : null,
    /* Il bot non ha una curva del capitale campionata: ha i saldi ai due
       estremi. Disegnare una retta fra due punti fingerebbe un andamento. */
    equity: null,
    drawdownMax: null,
    coppia: `${bot.asset}/${bot.currency}`,
    /* Il periodo parte dalla prima operazione quando questa PRECEDE l'avvio del bot.
       
       Succede davvero, ed e' il caso del bot in produzione: sostituendo un bot con un altro
       sullo stesso wallet, il nuovo eredita la posizione che il vecchio aveva aperto. Il record
       del bot 12 nasce il 30 agosto, ma l'operazione che ha in mano e' del 24: dichiarare
       «30 ago – 1 set» accanto a un marcatore del 24 significherebbe mostrare un'operazione
       fuori dal periodo che si sta dichiarando, e chi guarda non capirebbe quale delle due date
       e' sbagliata. */
    periodo: etichettaPeriodo(
      primaOperazione(operazioni, bot.startDate ?? bot.createdAt),
      bot.endDate ?? new Date().toISOString(),
    ),
    operazioniDichiarate: bot.trades?.length ?? null,
  };
}

/** La piu' antica fra l'avvio dichiarato e la prima operazione: un'operazione non puo' cadere
 *  fuori dal periodo che la contiene. */
function primaOperazione(
  operazioni: OperazioneNormalizzata[],
  avvio?: string | null,
): string | null {
  const primaEntrata = operazioni[0]?.entrataIl ?? null;
  if (!primaEntrata) return avvio ?? null;
  if (!avvio) return primaEntrata;
  return new Date(primaEntrata).getTime() < new Date(avvio).getTime() ? primaEntrata : avvio;
}

/** Quante sono chiuse, quante aperte, come è andata. */
export function riepilogoOperazioni(operazioni: OperazioneNormalizzata[]) {
  let migliore: OperazioneNormalizzata | null = null;
  let peggiore: OperazioneNormalizzata | null = null;
  let vinte = 0;
  let chiuse = 0;
  let somma = 0;

  for (const o of operazioni) {
    if (o.esito == null) continue;
    chiuse++;
    somma += o.esito;
    if (o.esito > 0) vinte++;
    if (!migliore || o.esito > (migliore.esito as number)) migliore = o;
    if (!peggiore || o.esito < (peggiore.esito as number)) peggiore = o;
  }

  return {
    totali: operazioni.length,
    chiuse,
    aperte: operazioni.length - chiuse,
    vinte,
    perse: chiuse - vinte,
    migliore,
    peggiore,
    media: chiuse > 0 ? somma / chiuse : null,
    percentualeVinte: chiuse > 0 ? (100 * vinte) / chiuse : null,
  };
}
